"use client";

import React from "react";
import { useGetAllProjectGalleries } from "../../hooks/use-get-all-galleries";
import type {
  ProjectGalleryContextValue,
  ProjectGalleryProviderProps,
} from "./types";

const initialData: ProjectGalleryContextValue = {
  projects: null,
  isLoading: true,
};

const ProjectGalleryContext = React.createContext(initialData);

function useProjectGallery() {
  return React.useContext(ProjectGalleryContext);
}

function ProjectGalleryProvider({ children }: ProjectGalleryProviderProps) {
  const result = useGetAllProjectGalleries();

  const isLoading = React.useMemo(() => {
    return result.some(r => r.isLoading);
  }, [result]);

  const projects = React.useMemo(() => {
    if (result.some(r => r.isLoading)) {
      return null;
    }

    if (result.length === 0) {
      return null;
    }

    const formatted = result.map(({ data }) => data ?? {});

    return formatted.reduce(
      (acc, cur) => ({
        ...acc,
        ...cur,
      }),
      {},
    );
  }, [result]);

  const context = React.useMemo(
    () => ({ projects, isLoading }),
    [projects, isLoading],
  );

  return (
    <ProjectGalleryContext.Provider value={context}>
      {children}
    </ProjectGalleryContext.Provider>
  );
}

export default ProjectGalleryProvider;
export { useProjectGallery };
